import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Phone, LogIn, Package, Loader2, Play } from "lucide-react";
import { api, COMPANY, fmtDate, mediaUrl } from "@/lib/apiClient";
import { Logo } from "@/components/Logo";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

export default function LandingPage() {
  const [jo, setJo] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [media, setMedia] = useState([]);

  useEffect(() => {
    api
      .get("/landing-media")
      .then((r) => setMedia(r.data))
      .catch(() => setMedia([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!jo.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(jo.trim())}`);
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  const images = media.filter((m) => m.media_type === "image");
  const videos = media.filter((m) => m.media_type === "video");

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="landing-page">
      <div className="absolute inset-0 grid-lines opacity-60" />
      <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full bg-blue-600/20 blur-[140px]" />
      <div className="absolute top-1/3 right-0 w-[460px] h-[460px] rounded-full bg-indigo-600/15 blur-[150px]" />

      <header className="relative z-10 flex items-center justify-between px-5 md:px-12 py-6">
        <Logo size={52} />
        <div className="flex items-center gap-3">
          <a
            href={COMPANY.waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-wa-link"
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20 transition-colors duration-300"
          >
            <Phone className="w-4 h-4" /> {COMPANY.phone}
          </a>
          <Link to="/login">
            <Button
              data-testid="header-login-btn"
              className="rounded-full bg-blue-600 hover:bg-blue-500 px-5 lift"
            >
              <LogIn className="w-4 h-4 mr-2" /> Log In
            </Button>
          </Link>
        </div>
      </header>

      <section className="relative z-10 px-5 md:px-12 pt-6 md:pt-10 max-w-5xl mx-auto">
        <div className="rounded-3xl border border-blue-400/20 bg-gradient-to-br from-blue-950/70 via-slate-900/60 to-slate-950/70 p-8 md:p-12 fade-up">
          <p className="uppercase tracking-[0.25em] text-[11px] md:text-xs font-semibold text-blue-400">
            Gapura PAS Percetakan
          </p>
          <h1
            data-testid="welcome-heading"
            className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.08] metal-text"
          >
            {COMPANY.welcome}
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-300 italic" data-testid="welcome-sub">
            {COMPANY.welcomeSub}
          </p>
        </div>
      </section>

      <main className="relative z-10 px-5 md:px-12 pb-16 pt-10 max-w-5xl mx-auto">
        <div className="glass rounded-3xl p-7 md:p-10 fade-up" data-testid="tracking-panel">
          <h2 className="font-display text-2xl md:text-3xl font-bold">
            Lacak progres <span className="text-blue-300">cetakan Anda</span>
          </h2>
          <p className="mt-3 text-sm md:text-base text-slate-400 max-w-xl leading-relaxed">
            Masukkan nomor Job Order yang tertera pada nota Anda untuk melihat status
            terkini pekerjaan cetak.
          </p>

          <form onSubmit={submit} className="mt-7">
            <div className="glass glow-ring rounded-2xl flex items-center gap-3 p-2 pl-5 border-blue-400/20">
              <Search className="w-5 h-5 text-blue-400 shrink-0" />
              <input
                data-testid="tracking-input"
                value={jo}
                onChange={(e) => setJo(e.target.value)}
                placeholder="Contoh: 1024"
                className="flex-1 bg-transparent outline-none text-lg md:text-2xl font-display py-3 placeholder:text-slate-600"
              />
              <Button
                type="submit"
                data-testid="track-submit-btn"
                disabled={loading}
                className="rounded-xl bg-blue-600 hover:bg-blue-500 h-12 px-6 font-semibold"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cek"}
              </Button>
            </div>
          </form>

          {error && (
            <div
              data-testid="track-error"
              className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-5 py-4 text-sm text-red-300"
            >
              {error}
            </div>
          )}

          {result && (
            <div data-testid="track-result" className="mt-8 rounded-2xl border border-white/10 bg-slate-950/50 p-6 md:p-8 fade-up">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400">
                    Nomor Job Order
                  </div>
                  <div className="font-display text-3xl font-bold mt-1" data-testid="result-jo-number">
                    {result.jo_number}
                  </div>
                </div>
                <StatusBadge status={result.status} testId="result-status" />
              </div>
              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Tanggal Order</div>
                  <div className="mt-1 text-slate-200" data-testid="result-date">
                    {fmtDate(result.tanggal)}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Deskripsi Pekerjaan</div>
                  <div className="mt-1 text-slate-200" data-testid="result-desc">
                    {result.deskripsi}
                  </div>
                </div>
              </div>
              {result.kategori?.length > 0 && (
                <div className="mt-7">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">
                    Progres per Kategori
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="result-kategori">
                    {result.kategori.map((k) => (
                      <span
                        key={k.kategori}
                        className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-slate-900/70 px-3 py-1.5 text-xs"
                      >
                        <span className="font-semibold text-slate-200">{k.kategori}</span>
                        <span className="text-blue-300">{k.status}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {result.history?.length > 0 && (
                <div className="mt-8 border-t border-white/10 pt-6">
                  <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-4">
                    Riwayat Status
                  </div>
                  <ol className="space-y-3">
                    {result.history.map((h) => (
                      <li key={`${h.at}-${h.status}`} className="flex items-center gap-3 text-sm">
                        <Package className="w-4 h-4 text-blue-400 shrink-0" />
                        <span className="text-slate-200 font-medium">{h.status}</span>
                        <span className="text-slate-500">
                          {new Date(h.at).toLocaleString("id-ID")}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {(images.length > 0 || videos.length > 0) && (
        <section className="relative z-10 px-5 md:px-12 pb-20 max-w-6xl mx-auto" data-testid="landing-gallery">
          <p className="uppercase tracking-[0.25em] text-xs font-semibold text-blue-400">
            Galeri PAS Percetakan
          </p>
          <h2 className="mt-3 font-display text-2xl md:text-3xl font-bold">
            Hasil kerja & <span className="metal-text">momen kami</span>
          </h2>

          {images.length > 0 && (
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {images.map((m) => (
                <figure
                  key={m.id}
                  data-testid={`gallery-image-${m.id}`}
                  className="group overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 lift"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={mediaUrl(m)}
                      alt={m.title || "Galeri PAS"}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  {m.title && (
                    <figcaption className="px-4 py-3 text-sm text-slate-300 truncate">
                      {m.title}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}

          {videos.length > 0 && (
            <div className="mt-10">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-400">
                <Play className="w-4 h-4 text-blue-400" /> Video
              </div>
              <div className="mt-5 grid gap-6 lg:grid-cols-2">
                {videos.map((m) =>
                  m.source === "upload" ? (
                    <video
                      key={m.id}
                      data-testid={`gallery-video-${m.id}`}
                      src={mediaUrl(m)}
                      controls
                      className="w-full rounded-2xl border border-white/10 bg-black"
                    />
                  ) : (
                    <a
                      key={m.id}
                      data-testid={`gallery-video-${m.id}`}
                      href={m.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="glass lift rounded-2xl p-6 flex items-center gap-4"
                    >
                      <span className="w-12 h-12 rounded-xl bg-blue-600/20 border border-blue-400/30 flex items-center justify-center">
                        <Play className="w-5 h-5 text-blue-300" />
                      </span>
                      <span className="text-slate-200">{m.title || "Tonton video"}</span>
                    </a>
                  )
                )}
              </div>
            </div>
          )}
        </section>
      )}

      <footer className="relative z-10 border-t border-white/5 px-5 md:px-12 py-10 max-w-5xl mx-auto">
        <div className="grid gap-6 sm:grid-cols-2 text-sm text-slate-400">
          <div className="flex gap-3">
            <MapPin className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
            <span data-testid="footer-address">{COMPANY.address}</span>
          </div>
          <a
            href={COMPANY.waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-phone"
            className="flex gap-3 sm:justify-end text-emerald-300 hover:text-emerald-200 transition-colors duration-300"
          >
            <Phone className="w-5 h-5 shrink-0 mt-0.5" />
            <span>{COMPANY.phone} · Chat WhatsApp</span>
          </a>
        </div>
      </footer>
    </div>
  );
}
